import {
  ChangeDetectorRef,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from "@angular/core";
import { Title } from "@angular/platform-browser";
import { ActivatedRoute, Router } from "@angular/router";
import { Location } from "@angular/common";
import { FormBuilder, FormGroup } from "@angular/forms";

import { select, Store } from "@ngrx/store";
import { Observable, Subject, Subscription, zip } from "rxjs";
import { skip } from "rxjs/operators";
import { TranslateService } from "@ngx-translate/core";
import { cloneDeep } from "lodash";

import { CustomerPartyDTO } from "src/app/models/Party/customerPartyDTO";
import { CountryDTO } from "src/app/models/Country/countryDTO";
import { PartySalutationDTO } from "src/app/models/Party/partySalutationDTO";
import { CategoryDTO } from "src/app/models/Categories/categoryDTO";
import { PropertyDTO } from "src/app/models/Properties/propertyDTO";
import {
  createCustomerParty,
  getCategoriesByUsage,
  getCountries,
  getCustomerParty,
  getPropertiesByUsage,
  getSalutations,
  resetCustomerParty,
  updateCustomerParty,
} from "src/app/store/actions/parties/parties.actions";
import {
  AppState,
  getLoadingPartyState,
  loadedCategoriesPartyState,
  loadedCustomerPartyState,
  loadedPropertiesPartyState,
  loadedTenantState,
  partiesStateCountries,
  partiesStateCreatedParty,
  partiesStateSalutations,
} from "src/app/store/reducers";
import { SnackbarService } from "src/app/shared/services/snackbar/snackbar.service";
import { SnackbarType } from "src/app/models/snackbar/snackbar.model";
import { CategoriesAndPropsUtils } from "src/app/shared/utils/categoriesAndPropsUtils";
import { TopCard } from "src/app/models/top-card/top-card.model";

import { BaseParty } from "src/app/shared/base-classes/base-party/base-party";

import { CanComponentDeactivate } from "src/app/shared/guard/pending.guard";
import { MatDialog } from "@angular/material/dialog";
import { ConfirmationDialogComponent } from "src/app/shared/modules/dialogs/confirmation-dialog/confirmation-dialog.component";
import {
  DefaultConfirmationDialogData,
  Dialog,
  GuardConfirmationDialogData,
} from "src/app/models/dialog/dialog.model";
import { isAuthenticated } from "src/app/shared/utils/authenticationUtils";

const USAGE_CODE = "CUSTOMER";

//Customer Detail Form
export const PARTY_FORM_PARAMS = {
  statusCode: "statusCode",
  blockedReason: "blockedReason",
  code: "code",
  vatin: "vatin",
  name: "name",
  birthday: "birthday",
  salutationCode: "salutationCode",
  contactName: "contactName",
  phone: "phone",
  email: "email",
  mobile: "mobile",
  description: "description",
  street: "street",
  postalZipCode: "postalZipCode",
  city: "city",
  countryCode: "countryCode",
  categories: "categories",
  properties: "properties",
};

@Component({
  selector: "app-customer-detail",
  templateUrl: "./customer-detail.component.html",
  styleUrls: ["./customer-detail.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class CustomerDetailComponent
  extends BaseParty
  implements OnInit, OnDestroy, CanComponentDeactivate
{
  //Customer
  isLoading$: Observable<boolean>;
  loadedCustomer$: Observable<CustomerPartyDTO>;
  created$: Observable<boolean>;

  //top-card configuration
  card: TopCard;
  icon = "add";

  //Subscriptions
  loadedCustomerSubs: Subscription;
  createdSub: Subscription;
  multiSubs: Subscription;

  //Salutations List
  salutationsList$: Observable<PartySalutationDTO[]>;
  salutationsList: PartySalutationDTO[];

  //Countries List
  countriesList$: Observable<CountryDTO[]>;
  countriesList: CountryDTO[];

  //Category List
  categoryList$: Observable<CategoryDTO[]>;
  categoryList: CategoryDTO[];

  //Property List
  propertyList$: Observable<PropertyDTO[]>;
  propertyList: PropertyDTO[];

  //Navigation
  currCode: string;

  currentCustomer: CustomerPartyDTO;

  //Actions
  creation = false;
  duplication = false;

  //Form
  customerDataForm: FormGroup;

  //Language
  langSubscription: Subscription;

  isChangesSaved: boolean;
  allowNavigation: Subject<boolean> = new Subject<boolean>();

  constructor(
    private translate: TranslateService,
    private titleService: Title,
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private store: Store<AppState>,
    private snackbarService: SnackbarService,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    fb: FormBuilder
  ) {
    super(fb);

    this.titleService.setTitle(this.translate.instant("Customers.gridTitle"));

    this.tenantData$ = this.store.pipe(select(loadedTenantState));
    this.isLoading$ = this.store.pipe(select(getLoadingPartyState));
    this.loadedCustomer$ = this.store.pipe(select(loadedCustomerPartyState));
    this.created$ = this.store.pipe(select(partiesStateCreatedParty));
    this.salutationsList$ = this.store.pipe(select(partiesStateSalutations));
    this.countriesList$ = this.store.pipe(select(partiesStateCountries));
    this.categoryList$ = this.store.pipe(select(loadedCategoriesPartyState));
    this.propertyList$ = this.store.pipe(select(loadedPropertiesPartyState));

    //initialize card configuration
    this.card = {
      avatarUrl: "",
      content: "",
      imageUrl: "",
      current: "",
      title: "Customers.newCustomer",
      subtitle: this.duplication
        ? "Customers.duplication"
        : "Customers.creation",
    };
  }

  confirmDialog(dialog: any, refresh = false): Observable<boolean> {
    const dialogData: Dialog = GuardConfirmationDialogData;
    const dialogRef = dialog.open(ConfirmationDialogComponent, dialogData);
    if (!refresh) {
      return dialogRef.afterClosed();
    }
    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.reload();
      }
    });
  }

  ngOnInit(): void {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.router.onSameUrlNavigation = "ignore";

    this.route.params.subscribe((params) => {
      const code = params.id;
      this.creation = false;
      this.duplication = false;
      const duplicateCode = localStorage.getItem("duplicate");

      this.router.routeReuseStrategy.shouldReuseRoute = () => false;
      if (code && code.length) {
        this.initialiseState(code, false);
      } else if (duplicateCode !== null) {
        this.duplication = true;
        localStorage.removeItem("duplicate");
        this.initialiseState(duplicateCode, true);
      } else {
        this.creation = true;
        this.initialiseState("", false);
      }
    });

    this.loadTenantDateFomat();

    //Initialize FormGroup
    this.createCustomerForm();
    this.loadCustomerInfo();
    this.loadLangChanges();
    this.resetCreation();
  }

  ngAfterViewChecked() {
    this.cdr.detectChanges();
  }

  resetCreation() {
    this.createdSub = this.created$.pipe(skip(1)).subscribe((created) => {
      if (created) {
        this.creation = false;
        this.duplication = false;
      }
    });
  }

  createCustomerForm() {
    this.customerDataForm = this.buildPartyForm();
    this.onStatusChanges(this.customerDataForm);
  }

  loadLangChanges() {
    this.langSubscription = this.translate.onLangChange.subscribe(() => {
      this.titleService.setTitle(this.translate.instant("Customers.gridTitle"));
    });
  }

  @HostListener("window:beforeunload")
  canDeactivate(): Observable<boolean> | boolean {
    return this.isNavigationAllowed();
  }

  isNavigationAllowed(): Observable<boolean> | boolean {
    if (!isAuthenticated(this.router.url) || this.customerDataForm.pristine) {
      return true;
    } else {
      return false;
    }
  }

  loadCustomerInfo() {
    this.multiSubs = zip(
      this.salutationsList$.pipe(skip(1)),
      this.countriesList$.pipe(skip(1)),
      this.categoryList$.pipe(skip(1)),
      this.propertyList$.pipe(skip(1))
    ).subscribe(([salutations, countries, categories, properties]) => {
      if (salutations && salutations.length) {
        this.salutationsList = salutations.map(({ code, description }) => ({
          code,
          description,
        }));
      }
      
      if (countries && countries.length) {
        this.countriesList = countries.map((country) => country);
      }

      this.loadedCustomerSubs = this.loadedCustomer$.subscribe(
        (customer: CustomerPartyDTO) => {
          if (customer) {
            this.currentCustomer = cloneDeep(customer);
            const { code, customerCode } = customer;


            if (
              (!this.currCode || (this.currCode && !this.currCode)) &&
              !this.duplication
            ) {
              this.currCode = this.currentCustomer.code;
            }

            this.card = {
              avatarUrl: "",
              content: "",
              imageUrl: "",
              current: !this.duplication ? code : "",
              title: !this.duplication
                ? "[" +
                  customerCode +
                  "] - " +
                  (customer.code?.length ? customer.name : "")
                : "Customers.newCustomer",
              subtitle: !this.duplication
                ? "Customers.customer"
                : "Customers.duplication",
              status: this.setCustomStatusChip(customer) ?? null,
            };

            this.setBasePartyForm(
              this.customerDataForm,
              customer,
              customerCode,
              this.duplication,
              this.creation
            );

            if (!this.creation && !this.duplication) {
              this.lockCode(this.customerDataForm);
            }
          }

          // Set Categories and Properties
          if (customer || this.creation || this.duplication) {
            if (categories && categories.length) {
              this.categoryList = categories;
            }
            if (properties && properties.length) {
              this.propertyList = properties;
            }
          }
        }
      );
    });

    this.store.dispatch(getSalutations(null));
    this.store.dispatch(getCountries(null));
    this.store.dispatch(
      getCategoriesByUsage({
        pageReq: {
          query: "is_enabled==1",
          limit: undefined,
          offset: 0,
          realSize: true,
        },
        usageCode: USAGE_CODE,
      })
    );
    this.store.dispatch(
      getPropertiesByUsage({
        pageReq: {
          query: "is_enabled==1",
          limit: undefined,
          offset: 0,
          realSize: true,
        },
        usageCode: USAGE_CODE,
      })
    );
  }

  initialiseState(code: string, duplicate?: boolean) {
    if (code) {
      this.loadCustomer(code);
      if (duplicate) {
        this.currCode = "";
      } else {
        this.currCode = code;
      }
    } else {
      this.reset();
      this.currCode = "";
      this.card = {
        avatarUrl: "",
        content: "",
        imageUrl: "",
        current: "",
        title: "Customers.newCustomer",
        subtitle: this.duplication
          ? "Customers.duplication"
          : "Customers.creation",
      };
    }
  }

  loadCustomer(code: string) {
    if (code) {
      this.store.dispatch(getCustomerParty({ partyCode: code }));
    }
  }

  reset() {
    this.store.dispatch(resetCustomerParty());
  }

  back() {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.location.back();
  }

  onSubmit() {
    if (!this.customerDataForm.valid) {
      this.customerDataForm.markAllAsTouched();
      this.snackbarService.show({
        message: this.translate.instant("Customers.CustomerFormInvalid"),
        type: SnackbarType.ERROR,
      });
      return;
    }

    if (this.customerDataForm.pristine && !this.creation && !this.duplication) {
      this.snackbarService.show({
        message: this.translate.instant("Customers.NoChanges"),
        type: SnackbarType.WARNING,
      });
      return;
    }

    let customer: CustomerPartyDTO = {
      statusCode:
        this.customerDataForm.controls[PARTY_FORM_PARAMS.statusCode].value ??
        null,
      blockedReason:
        this.customerDataForm.get(PARTY_FORM_PARAMS.statusCode).value ===
        "BLOCKED"
          ? this.customerDataForm.controls[PARTY_FORM_PARAMS.blockedReason]
              .value
          : null,
      customerCode:
        this.customerDataForm.controls[PARTY_FORM_PARAMS.code].value ?? null,
      name:
        this.customerDataForm.controls[PARTY_FORM_PARAMS.name].value ?? null,
      birthday:
        this.customerDataForm.controls[PARTY_FORM_PARAMS.birthday].value ??
        null,
      vatin:
        this.customerDataForm.controls[PARTY_FORM_PARAMS.vatin].value ?? null,
      salutationCode:
        this.customerDataForm.controls[PARTY_FORM_PARAMS.salutationCode]
          .value ?? null,
      additionalData: {
        contactName:
          this.customerDataForm.controls[PARTY_FORM_PARAMS.contactName].value ??
          null,
      },
      contacts: this.setFormContacts(
        this.customerDataForm,
        this.currentCustomer
      ),
      addresses: this.setFormAddresses(
        this.customerDataForm,
        this.currentCustomer
      ),
      categories: CategoriesAndPropsUtils.setFormCategories(
        this.customerDataForm.controls.categories.value
      ),
      properties: CategoriesAndPropsUtils.setFormProperties(
        this.customerDataForm.controls.properties.value
      ),
    };

    if (!this.currCode.length) {
      this.store.dispatch(createCustomerParty({ party: customer }));
    } else {
      customer = { ...customer, code: this.currCode };
      this.store.dispatch(updateCustomerParty({ party: customer }));
    }

    this.customerDataForm.markAsUntouched();
    this.customerDataForm.markAsPristine();
  }

  refresh() {
    if (isAuthenticated(this.router.url) && !this.customerDataForm.pristine) {
      this.confirmDialog(this.dialog, true);
      return;
    } else {
      this.reload();
    }
  }
  
  reload() {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.router.onSameUrlNavigation = "reload";
    this.router.navigate(["customers/customer", this.currCode]);
  }

  createNew() {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.router.onSameUrlNavigation = "reload";
    this.router.navigate(["/customers/customer"]);
    this.creation = true;
  }

  duplicate() {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    localStorage.setItem("duplicate", this.currCode);
    this.router.onSameUrlNavigation = "reload";
    this.router.navigate(["customers/customer", {}]);
  }

  ngOnDestroy(): void {
    if (this.createdSub) {
      this.createdSub.unsubscribe();
    }
    if (this.tenantDataSub) {
      this.tenantDataSub.unsubscribe();
    }
    if (this.multiSubs) {
      this.multiSubs.unsubscribe();
    }
    if (this.loadedCustomerSubs) {
      this.loadedCustomerSubs.unsubscribe();
    }
    if (this.langSubscription) {
      this.langSubscription.unsubscribe();
    }
  }
}
